import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, User, Search, Upload, X, Eye, History, Calendar, StickyNote, MapPin, CalendarCheck, CalendarOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { modelsApi } from "@/lib/unifiedApi";
import { storageService } from "@/lib/storage";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const COLOR_OPTIONS = [
  { name: "Lime", value: "#BFFF00" },
  { name: "Red", value: "#EF4444" },
  { name: "Blue", value: "#3B82F6" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Pink", value: "#EC4899" },
  { name: "Orange", value: "#F97316" },
  { name: "Teal", value: "#14B8A6" },
  { name: "Yellow", value: "#EAB308" },
  { name: "Gray", value: "#6B7280" },
  { name: "Indigo", value: "#6366F1" },
];

const ModelsPage = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [viewLogDialogOpen, setViewLogDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [selectedModelForLog, setSelectedModelForLog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    model_name: "",
    size: "",
    tag_color: "#BFFF00",
    pc_model: "",
    image_url: "",
    available_for_event: true,
    logs: [],
  });

  const [logFormData, setLogFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    action: "",
    note: "",
  });

  useEffect(() => {
    const unsubscribe = modelsApi.subscribe((data) => {
      setModels(data);
      setLoading(false);
    });
    const timeout = setTimeout(() => setLoading(false), 2000);
    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingModel) {
        await modelsApi.update(editingModel.id, formData);
        toast.success("Model updated");
      } else {
        const initialLog = {
          id: `log-${Date.now()}`,
          date: format(new Date(), "yyyy-MM-dd"),
          action: "เพิ่มข้อมูล Model ใหม่",
          note: `สร้าง Model: ${formData.model_name}`,
          type: "initial",
        };
        await modelsApi.create({ ...formData, logs: [initialLog] });
        toast.success("Model added");
      }
      resetForm();
    } catch (error) {
      console.error("Error saving model:", error);
      toast.error("Failed to save model");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ลบ Model นี้?")) return;
    try {
      await modelsApi.delete(id);
      toast.success("Model deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      model_name: model.model_name || "",
      size: model.size || "",
      tag_color: model.tag_color || "#BFFF00",
      pc_model: model.pc_model || "",
      image_url: model.image_url || "",
      available_for_event: model.available_for_event !== false,
      logs: model.logs || [],
    });
    setDialogOpen(true);
  };

  const toggleAvailable = async (model) => {
    const next = model.available_for_event === false; // currently off → turn on
    try {
      await modelsApi.update(model.id, { available_for_event: next });
      toast.success(next ? `${model.model_name} พร้อมใช้ในอีเวนต์` : `${model.model_name} ปิดใช้ในอีเวนต์`);
    } catch {
      toast.error("อัปเดตไม่สำเร็จ");
    }
  };

  const resetForm = () => {
    setEditingModel(null);
    setFormData({
      model_name: "",
      size: "",
      tag_color: "#BFFF00",
      pc_model: "",
      image_url: "",
      available_for_event: true,
      logs: [],
    });
    setDialogOpen(false);
    setUploadProgress(0);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return;
    }
    setUploading(true);
    try {
      const result = await storageService.uploadFile(file, 'models', (progress) => {
        setUploadProgress(progress);
      });
      setFormData({ ...formData, image_url: result.url });
      toast.success('อัพโหลดสำเร็จ');
    } catch (error) {
      toast.error('อัพโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image_url: "" });
  };

  // Log management
  const openAddLog = (model) => {
    setSelectedModelForLog(model);
    setLogFormData({ date: format(new Date(), "yyyy-MM-dd"), action: "", note: "" });
    setLogDialogOpen(true);
  };

  const openViewLogs = (model) => {
    setSelectedModelForLog(model);
    setViewLogDialogOpen(true);
  };

  const handleAddLog = async (e) => {
    e.preventDefault();
    if (!selectedModelForLog) return;
    try {
      const newLog = {
        id: `log-${Date.now()}`,
        date: logFormData.date,
        action: logFormData.action,
        note: logFormData.note,
        type: "activity",
      };
      const updatedLogs = [...(selectedModelForLog.logs || []), newLog];
      await modelsApi.update(selectedModelForLog.id, { ...selectedModelForLog, logs: updatedLogs });
      toast.success("เพิ่ม Log สำเร็จ");
      setLogDialogOpen(false);
      setSelectedModelForLog(null);
    } catch (error) {
      toast.error("เพิ่ม Log ไม่สำเร็จ");
    }
  };

  const handleDeleteLog = async (model, logId) => {
    if (!window.confirm("ลบ Log นี้?")) return;
    try {
      const updatedLogs = (model.logs || []).filter(log => log.id !== logId);
      await modelsApi.update(model.id, { ...model, logs: updatedLogs });
      setSelectedModelForLog({ ...model, logs: updatedLogs });
      toast.success("ลบ Log สำเร็จ");
    } catch (error) {
      toast.error("ลบ Log ไม่สำเร็จ");
    }
  };

  const filteredModels = models.filter(
    (model) =>
      model.model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.pc_model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="page-container" data-testid="models-loading">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="models-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Models</h1>
          <p className="text-gray-500 mt-1">จัดการข้อมูล Model และประวัติการใช้งาน</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-model-btn"
              className="bg-[#BFFF00] hover:bg-[#a6e600] text-black font-medium rounded-full"
              onClick={() => resetForm()}
            >
              <Plus size={18} className="mr-2" />
              Add Model
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900">
                {editingModel ? "แก้ไข Model" : "เพิ่ม Model ใหม่"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="text-gray-700">รูปภาพ Model</Label>
                {formData.image_url ? (
                  <div className="relative w-full h-40 rounded-lg overflow-hidden bg-gray-100">
                    <img src={formData.image_url} alt="Model" className="w-full h-full object-cover" />
                    <button type="button" onClick={removeImage} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                    {uploading ? (
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                        <span className="text-sm text-gray-500">{Math.round(uploadProgress)}%</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-500">คลิกเพื่ออัพโหลดรูปภาพ</span>
                        <span className="text-xs text-gray-400 mt-1">ขนาดไม่เกิน 5MB</span>
                      </>
                    )}
                  </label>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">ชื่อ Model</Label>
                <Input value={formData.model_name} onChange={(e) => setFormData({ ...formData, model_name: e.target.value })} placeholder="ใส่ชื่อ Model" required className="border-gray-200" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">ขนาด</Label>
                <Input value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} placeholder="เช่น S, M, L, XL หรือ 170cm" className="border-gray-200" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">PC Model</Label>
                <Input value={formData.pc_model} onChange={(e) => setFormData({ ...formData, pc_model: e.target.value })} placeholder="รหัส PC Model" className="border-gray-200" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">สี Tag</Label>
                <div className="grid grid-cols-5 gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, tag_color: color.value })}
                      className={`w-full h-10 rounded-lg border-2 transition-all ${formData.tag_color === color.value ? "border-gray-900 scale-105" : "border-transparent hover:scale-105"}`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-sm text-gray-500">กำหนดเอง:</span>
                  <Input type="text" value={formData.tag_color} onChange={(e) => setFormData({ ...formData, tag_color: e.target.value })} placeholder="#BFFF00" className="w-28 font-mono text-sm border-gray-200" />
                  <input type="color" value={formData.tag_color} onChange={(e) => setFormData({ ...formData, tag_color: e.target.value })} className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200" />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-gray-200">ยกเลิก</Button>
                <Button type="submit" className="flex-1 bg-[#BFFF00] hover:bg-[#a6e600] text-black">{editingModel ? "อัพเดท" : "เพิ่ม Model"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input placeholder="ค้นหา Model..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white border-gray-200 rounded-xl" />
      </div>

      {/* Models Grid */}
      {filteredModels.length === 0 ? (
        <Card className="bg-white border-gray-100 shadow-sm rounded-2xl">
          <CardContent className="p-12 text-center">
            <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">ไม่พบ Model</p>
            <p className="text-sm text-gray-400 mt-1">เพิ่ม Model แรกของคุณเพื่อเริ่มต้น</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredModels.map((model) => (
            <Card key={model.id} data-testid={`model-card-${model.id}`} className={`bg-white border-gray-100 shadow-sm rounded-2xl hover:shadow-md transition-shadow ${model.available_for_event === false ? "opacity-60" : ""}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  {model.image_url ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      <img src={model.image_url} alt={model.model_name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-3 h-16 rounded-full flex-shrink-0" style={{ backgroundColor: model.tag_color || "#BFFF00" }} />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: model.tag_color || "#BFFF00", color: "#000" }}>
                        {model.size || "N/A"}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                        {(model.logs || []).length} logs
                      </span>
                      {model.available_for_event === false && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-medium">ปิดใช้งาน</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 text-lg truncate">{model.model_name}</h3>
                    {model.pc_model && <p className="text-sm text-gray-500 mt-1 font-mono">PC: {model.pc_model}</p>}
                  </div>

                  <div className="flex flex-col gap-1">
                    <button onClick={() => openViewLogs(model)} className="p-2 rounded-lg hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-colors" title="ดู Log">
                      <Eye size={16} />
                    </button>
                    <button onClick={() => openAddLog(model)} className="p-2 rounded-lg hover:bg-emerald-50 text-gray-500 hover:text-emerald-600 transition-colors" title="เพิ่ม Log">
                      <History size={16} />
                    </button>
                    <button onClick={() => handleEdit(model)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors" title="แก้ไข">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDelete(model.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors" title="ลบ">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <span className="text-sm text-gray-600 flex items-center gap-1.5">
                    {model.available_for_event === false
                      ? <CalendarOff size={15} className="text-gray-400" />
                      : <CalendarCheck size={15} className="text-emerald-500" />}
                    Available for Event
                  </span>
                  <Switch
                    checked={model.available_for_event !== false}
                    onCheckedChange={() => toggleAvailable(model)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Log Dialog */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <History size={18} className="text-emerald-500" />
              เพิ่ม Log - {selectedModelForLog?.model_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLog} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><Calendar size={14} className="text-blue-500" />วันที่</Label>
              <Input type="date" value={logFormData.date} onChange={(e) => setLogFormData({ ...logFormData, date: e.target.value })} required className="border-gray-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><MapPin size={14} className="text-red-500" />กิจกรรม</Label>
              <Input value={logFormData.action} onChange={(e) => setLogFormData({ ...logFormData, action: e.target.value })} placeholder="เช่น เอาตู้ไปซ่อม, ส่งไปงาน, ทำความสะอาด" required className="border-gray-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><StickyNote size={14} className="text-amber-500" />รายละเอียด</Label>
              <Textarea value={logFormData.note} onChange={(e) => setLogFormData({ ...logFormData, note: e.target.value })} placeholder="รายละเอียดเพิ่มเติม..." className="border-gray-200 min-h-[80px]" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setLogDialogOpen(false)} className="flex-1 border-gray-200">ยกเลิก</Button>
              <Button type="submit" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">เพิ่ม Log</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Logs Dialog */}
      <Dialog open={viewLogDialogOpen} onOpenChange={setViewLogDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Eye size={18} className="text-blue-500" />
              ประวัติ Log - {selectedModelForLog?.model_name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="p-4 bg-gray-50 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">ขนาด:</span><span className="ml-2 text-gray-900">{selectedModelForLog?.size || "-"}</span></div>
                <div><span className="text-gray-500">PC Model:</span><span className="ml-2 text-gray-900">{selectedModelForLog?.pc_model || "-"}</span></div>
              </div>
            </div>
            <div className="flex justify-end mb-4">
              <Button size="sm" onClick={() => { setViewLogDialogOpen(false); openAddLog(selectedModelForLog); }} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <Plus size={16} className="mr-1" />เพิ่ม Log
              </Button>
            </div>
            <div className="space-y-3">
              {(selectedModelForLog?.logs || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500"><History className="w-10 h-10 mx-auto text-gray-300 mb-2" /><p>ยังไม่มี Log</p></div>
              ) : (
                [...(selectedModelForLog?.logs || [])].reverse().map((log) => (
                  <div key={log.id} className={`p-4 rounded-lg border ${log.type === 'initial' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.type === 'initial' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {log.type === 'initial' ? 'เริ่มต้น' : 'กิจกรรม'}
                          </span>
                          <span className="text-sm text-gray-500">{log.date ? format(parseISO(log.date), "d MMM yyyy") : "-"}</span>
                        </div>
                        <div className="text-sm text-gray-700 font-medium mb-1">{log.action}</div>
                        {log.note && <p className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">{log.note}</p>}
                      </div>
                      {log.type !== 'initial' && (
                        <button onClick={() => handleDeleteLog(selectedModelForLog, log.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelsPage;
